import React, { useEffect, useState } from "react";
import { CalendarCheck, CheckCircle2, XCircle, UserX, Users, Wallet, TrendingUp, Building2, Loader2, UserPlus } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { Skeleton } from "@/components/ui/skeleton";

const fmt = (n) => new Intl.NumberFormat("tr-TR").format(n);

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-[#181824] border border-[#2D2D3F] px-3 py-2 shadow-xl">
      <p className="text-[11px] text-[#68687B] mb-0.5">{label}</p>
      <p className="text-sm font-mono font-bold text-[#FF5500]">₺{fmt(payload[0].value)}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Kontrol Paneli" subtitle="Get Up Fit Tuzla — günlük operasyon özeti" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl bg-[#13131C]" />)}
        </div>
      </div>
    );
  }

  const { real, demo } = data;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <PageHeader title="Kontrol Paneli" subtitle="Get Up Fit Tuzla — günlük operasyon özeti" />

      {/* Real data */}
      <section>
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#68687B] mb-3">Canlı Veriler</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testId="stat-card-total-members" icon={Users} label="Toplam Üye" value={fmt(real.total_members)} sub={`${real.active_members} aktif · ${real.passive_members} pasif`} accent />
          <StatCard testId="stat-card-total-trainers" icon={Building2} label="Antrenör" value={fmt(real.total_trainers)} sub="Aktif kadro" />
          <StatCard testId="stat-card-total-services" icon={CalendarCheck} label="Hizmet" value={fmt(real.total_services)} sub="Tanımlı hizmet" />
          <StatCard testId="stat-card-total-packages" icon={Wallet} label="Paket" value={fmt(real.total_packages)} sub="Satışa açık paket" />
        </div>
      </section>

      {/* Today — real appointment + lead data, demo for revenue/occupancy */}
      <section>
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#68687B] mb-3">Bugün</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testId="stat-card-today-sessions" icon={CalendarCheck} label="Bugünkü Seanslar" value={real.today_sessions} />
          <StatCard testId="stat-card-today-completed" icon={CheckCircle2} label="Tamamlandı" value={real.today_completed} />
          <StatCard testId="stat-card-today-cancelled" icon={XCircle} label="İptal Edildi" value={real.today_cancelled} />
          <StatCard testId="stat-card-today-no-show" icon={UserX} label="Gelmedi" value={real.today_no_show} />
          <StatCard testId="stat-card-new-leads" icon={UserPlus} label="Yeni Aday" value={real.new_leads} />
          <StatCard testId="stat-card-new-members" icon={Users} label="Yeni Üye" value={demo.today.new_members} demo />
          <StatCard testId="stat-card-daily-revenue" icon={Wallet} label="Günlük Ciro" value={`₺${fmt(demo.today.daily_revenue)}`} demo />
          <StatCard testId="stat-card-occupancy" icon={TrendingUp} label="Doluluk" value={`%${demo.month.occupancy_rate}`} demo />
        </div>
      </section>

      {/* Charts */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl bg-[#13131C] border border-[#222230] p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-display font-bold text-white text-lg">Haftalık Ciro</h4>
              <p className="text-xs text-[#68687B]">Son 7 gün · demo veri</p>
            </div>
            <span className="font-mono font-extrabold text-2xl text-[#FF5500]">₺{fmt(demo.month.total_sales)}</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={demo.revenue_trend} margin={{ left: -18, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF5500" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#FF5500" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E1E2A" vertical={false} />
              <XAxis dataKey="label" stroke="#68687B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#68687B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="value" stroke="#FF5500" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl bg-[#13131C] border border-[#222230] p-5">
          <h4 className="font-display font-bold text-white text-lg mb-1">Hizmet Dağılımı</h4>
          <p className="text-xs text-[#68687B] mb-4">Bu ay · demo veri</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={demo.service_distribution} layout="vertical" margin={{ left: 20, right: 16 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="label" stroke="#A0A0B2" fontSize={11} tickLine={false} axisLine={false} width={70} />
              <Tooltip cursor={{ fill: "#1B1B27" }} contentStyle={{ background: "#181824", border: "1px solid #2D2D3F", borderRadius: 12 }} />
              <Bar dataKey="value" fill="#FF5500" radius={[0, 6, 6, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Trainer performance */}
      <section className="rounded-2xl bg-[#13131C] border border-[#222230] p-5">
        <div className="flex items-center gap-2 mb-4">
          <h4 className="font-display font-bold text-white text-lg">Antrenör Performansı</h4>
          <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30">Demo</span>
        </div>
        <div className="space-y-3">
          {demo.trainer_performance.map((t, i) => (
            <div key={i} className="flex items-center gap-4">
              <span className="font-mono text-sm text-[#68687B] w-5">{i + 1}</span>
              <span className="flex-1 text-sm font-medium text-white">{t.name}</span>
              <div className="flex-1 max-w-[200px] h-2 rounded-full bg-[#1B1B27] overflow-hidden">
                <div className="h-full bg-[#FF5500] rounded-full" style={{ width: `${(t.sessions / 90) * 100}%` }} />
              </div>
              <span className="font-mono text-sm text-white w-12 text-right">{t.sessions}</span>
              <span className="text-xs text-[#FF5500] font-semibold w-10 text-right">★{t.rating}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
